import { useState } from 'react';

import { Box, Button, Flex, Heading, SimpleGrid, Text } from '@chakra-ui/react';
import { AddIcon } from '@udacity/chakra-uds-icons';

import { ContentContainer } from '~/components/container';
import { Markdown } from '~/components/markdown';
import { Modal } from '~/components/modal';
import { SanityImage } from '~/components/sanity-image';

import { AboutUsTemplate } from '../model';

export function TeamMembersSection({ teamMembers }: { teamMembers: AboutUsTemplate['teamMembers'] }) {
  const [selectedTeamMember, setSelectedTeamMember] = useState<AboutUsTemplate['teamMembers'][number]>();

  return (
    <Box as='section'>
      <ContentContainer>
        <Flex flexDirection='column' gap='16px' textAlign='center'>
          <Heading as='h2' size='h1'>
            Our Team
          </Heading>
          <Text margin='auto' maxWidth='900px'>
            Our leadership team comes together with one goal: to help learners, organizations, and nations prepare for
            and strengthen the digital workforce of the next generation.
          </Text>
        </Flex>
        <Box h='48px' />
        <SimpleGrid gridTemplateColumns='repeat(auto-fill, minmax(min(256px, 100%), 1fr))' spacing='32px'>
          {teamMembers.map((teamMember) => (
            <Flex
              key={teamMember._id}
              alignItems='center'
              as='article'
              flexDirection='column'
              gap='8px'
              textAlign='center'
            >
              <Box height='256px' position='relative' width='100%'>
                <SanityImage
                  image={teamMember.image}
                  sizes='256px'
                  style={{ borderRadius: '4px', objectFit: 'cover' }}
                  fill
                />
              </Box>
              <Flex bg='gray.100' borderRadius='base' flex={1} flexDirection='column' width='100%'>
                <Flex borderBottom='solid 1px' borderColor='gray.600' flex={1} flexDirection='column' p='16px'>
                  <Heading aria-label={`Team member's name`} as='div' size='h3'>
                    {teamMember.name}
                  </Heading>
                  <Text aria-label={`Team member's job title`}>{teamMember.title}</Text>
                </Flex>
                <Button
                  rightIcon={<AddIcon h='24px' w='24px' />}
                  size='icon-right'
                  variant='ghost'
                  onClick={() => setSelectedTeamMember(teamMember)}
                >
                  Read Bio
                </Button>
              </Flex>
            </Flex>
          ))}
        </SimpleGrid>
      </ContentContainer>

      {selectedTeamMember && (
        <TeamMemberModal
          isOpen={Boolean(selectedTeamMember)}
          teamMember={selectedTeamMember}
          onClose={() => setSelectedTeamMember(undefined)}
        />
      )}
    </Box>
  );
}

function TeamMemberModal({
  teamMember,
  isOpen,
  onClose,
}: {
  teamMember: AboutUsTemplate['teamMembers'][number];
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Modal isOpen={isOpen} size='3xl' title={teamMember.name} onClose={onClose}>
      <Flex alignItems={{ base: 'center', md: 'flex-start' }} flexDirection={{ base: 'column', md: 'row' }} gap='40px'>
        <SanityImage
          height={250}
          image={teamMember.image}
          style={{ borderRadius: '4px', objectFit: 'cover' }}
          width={256}
        />
        <Markdown markdownText={teamMember.bio} />
      </Flex>
    </Modal>
  );
}
